/**
 * WhatsApp Cloud API request/response schemas — TypeBox.
 *
 * Source of truth: Meta Cloud API reference
 *  - https://developers.facebook.com/docs/whatsapp/cloud-api/reference/messages
 *
 * Story 2.1 establishes the wire-level shapes used by `WhatsAppService` in
 * apps/api. Other E2 stories (2.2 webhook, 2.3 admin templates, E3/E5
 * dispatchers) consume these types so the request body, the success
 * response and the Meta error envelope stay consistent across the codebase.
 *
 * Constitutional Article IV (No Invention): every property below maps to a
 * documented Meta field. Optional fields stay `Type.Optional` instead of
 * defaulting so we never silently invent values.
 */
import { Type } from '@sinclair/typebox';
// ----------------------------------------------------------------------------
// Template components (parameter substitution for HSM templates)
// ----------------------------------------------------------------------------
/**
 * Template parameter shape — Meta accepts `text`, `currency`, `date_time`,
 * `image`, `document`, `video`. For the MVP (FR4) we only use `text`; the
 * union keeps the door open for media parameters without inventing fields.
 */
export const WhatsAppTemplateParameter = Type.Union([
    Type.Object({
        type: Type.Literal('text'),
        text: Type.String(),
    }),
    Type.Object({
        type: Type.Literal('currency'),
        currency: Type.Object({
            fallback_value: Type.String(),
            code: Type.String(),
            amount_1000: Type.Integer(),
        }),
    }),
    Type.Object({
        type: Type.Literal('date_time'),
        date_time: Type.Object({
            fallback_value: Type.String(),
        }),
    }),
]);
export const WhatsAppTemplateComponent = Type.Object({
    type: Type.Union([
        Type.Literal('header'),
        Type.Literal('body'),
        Type.Literal('button'),
        Type.Literal('footer'),
    ]),
    parameters: Type.Optional(Type.Array(WhatsAppTemplateParameter)),
    sub_type: Type.Optional(Type.String()),
    index: Type.Optional(Type.Integer()),
});
// ----------------------------------------------------------------------------
// Request envelopes
// ----------------------------------------------------------------------------
const MessagingProduct = Type.Literal('whatsapp');
/** POST /v18.0/{phoneNumberId}/messages — `type: text` envelope. */
export const WhatsAppTextMessageRequest = Type.Object({
    messaging_product: MessagingProduct,
    to: Type.String({ pattern: '^\\+?\\d{10,15}$', description: 'E.164, with or without leading +' }),
    type: Type.Literal('text'),
    text: Type.Object({
        body: Type.String({ minLength: 1, maxLength: 4096 }),
        preview_url: Type.Optional(Type.Boolean()),
    }),
});
/** POST /v18.0/{phoneNumberId}/messages — `type: template` envelope. */
export const WhatsAppTemplateMessageRequest = Type.Object({
    messaging_product: MessagingProduct,
    to: Type.String({ pattern: '^\\+?\\d{10,15}$' }),
    type: Type.Literal('template'),
    template: Type.Object({
        name: Type.String({ minLength: 1 }),
        language: Type.Object({ code: Type.String({ minLength: 2 }) }),
        components: Type.Optional(Type.Array(WhatsAppTemplateComponent)),
    }),
});
/**
 * Discriminated union of every accepted request body. Consumers that route
 * across both message types (e.g. the audit logger) can narrow on `type`.
 */
export const WhatsAppMessageRequest = Type.Union([
    WhatsAppTextMessageRequest,
    WhatsAppTemplateMessageRequest,
]);
// ----------------------------------------------------------------------------
// Response envelopes
// ----------------------------------------------------------------------------
/** Success body returned by Meta on 200. */
export const WhatsAppMessageResponse = Type.Object({
    messaging_product: MessagingProduct,
    contacts: Type.Array(Type.Object({
        input: Type.String(),
        wa_id: Type.String(),
    })),
    messages: Type.Array(Type.Object({
        id: Type.String({ description: 'wamid that we persist as messages.meta_message_id' }),
        message_status: Type.Optional(Type.String()),
    })),
});
/**
 * Error body returned by Meta on 4xx/5xx. The error object always has at
 * least `code` and `message`; `error_subcode`, `fbtrace_id` and the
 * `error_data` envelope are optional per Meta's published reference.
 */
export const WhatsAppErrorResponse = Type.Object({
    error: Type.Object({
        message: Type.String(),
        type: Type.Optional(Type.String()),
        code: Type.Integer(),
        error_subcode: Type.Optional(Type.Integer()),
        fbtrace_id: Type.Optional(Type.String()),
        error_data: Type.Optional(Type.Object({
            messaging_product: Type.Optional(MessagingProduct),
            details: Type.Optional(Type.String()),
        })),
    }),
});
//# sourceMappingURL=whatsapp.js.map